import React from "react";
import { Stack, StackProps, Text } from "@bookingcom/bui-react";
import { useUnifiedPriceTagController, type UnifiedPriceTagControllerProps } from "./useUnifiedPriceTag.controller";
import { UnifiedPriceTagPriceBreakdown } from "./UnifiedPriceTagPriceBreakdown";
import { UnifiedPriceTagPricePerAdultExplanation } from "./UnifiedPriceTagPricePerAdultExplanation";
import { UnifiedPriceTagExplanation } from "./UnifiedPriceTagExplanation";
import styles from "./UnifiedPriceTag.module.css";
import useUserAgent from "../../../hooks/useUserAgent";
import flights_lve_web_fd_price_strikethrough_alignment from "utils/experiments/pricing/flights_lve_web_fd_price_strikethrough_alignment";
import useRouteName from "hooks/useRouteName";

export type UnifiedPriceTagProps = UnifiedPriceTagControllerProps & Pick<StackProps, "attributes">;

export const UnifiedPriceTag = (props: UnifiedPriceTagProps) => {
  const { formattedPrice, formattedStrikethroughPrice, priceWrapperStackProps, priceStackProps, priceTextProps } =
    useUnifiedPriceTagController(props);
  const {
    attributes,
    priceAlignment,
    priceLabel,
    strikethroughPriceProps,
    shouldShowPriceExplanationOnly,
    shouldShowPricePerAdult
  } = props;
  const routeName = useRouteName();
  const { isMobile } = useUserAgent();

  // The FD mobile change of the flights_lve_web_fd_price_strikethrough_alignment requires a different stack format. When the price is
  // too long, the strikethrough goes on top and the spacing changes, keeping the icon aligned with the new price.
  const isInPriceStrikethroughAlignmentExperiment =
    flights_lve_web_fd_price_strikethrough_alignment.trackWithDefaultStage() > 0;
  const inPriceStrikethroughAlignmentExperimentMobileFD =
    isInPriceStrikethroughAlignmentExperiment && isMobile && routeName.includes("flightdetails");

  // Extracted component for rendering strikethrough price
  const renderStrikethroughPrice = () => {
    if (!formattedStrikethroughPrice || shouldShowPricePerAdult) return null;
    return (
      <Text
        variant={strikethroughPriceProps?.variant || "body_2"}
        decoration="line-through"
        color="destructive"
        className={
          inPriceStrikethroughAlignmentExperimentMobileFD
            ? styles.priceInPriceAlignmentExperiment
            : styles.strikedthroughPrice
        }
        attributes={{ "data-testid": "upt_price_strikethrough" }}
      >
        {formattedStrikethroughPrice}
      </Text>
    );
  };

  // Render the price explanation only view
  if (shouldShowPriceExplanationOnly) {
    return (
      <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
        <Stack direction="row" justifyContent={priceAlignment}>
          <UnifiedPriceTagPricePerAdultExplanation {...props} />
          <UnifiedPriceTagPriceBreakdown {...props} />
        </Stack>
        <UnifiedPriceTagExplanation {...props} />
      </Stack>
    );
  }

  if (inPriceStrikethroughAlignmentExperimentMobileFD) {
    return (
      <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
        <Stack
          direction="row"
          alignItems="center"
          {...priceStackProps}
          justifyContent={priceAlignment}
          attributes={attributes}
          className={styles.priceContainer}
        >
          {renderStrikethroughPrice()}
          <Stack direction="row" alignItems="center" gap={0.5}>
            {priceLabel && <Text className={styles.noWrap}>{priceLabel}</Text>}
            <Text
              className={styles.price}
              {...priceTextProps}
              attributes={{ ...priceTextProps.attributes, "data-testid": "upt_price" }}
            >
              {formattedPrice}
            </Text>
            <UnifiedPriceTagPriceBreakdown {...props} />
          </Stack>
        </Stack>
        <UnifiedPriceTagPricePerAdultExplanation {...props} />
        <UnifiedPriceTagExplanation {...props} />
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={isInPriceStrikethroughAlignmentExperiment ? 0.5 : 1}
        {...priceWrapperStackProps}
        attributes={attributes}
      >
        <Stack {...priceStackProps}>
          {renderStrikethroughPrice()}
          <Stack direction="row" gap={1}>
            {priceLabel && <Text className={styles.noWrap}>{priceLabel}</Text>}
            <Text
              className={styles.price}
              {...priceTextProps}
              attributes={{ ...priceTextProps.attributes, "data-testid": "upt_price" }}
            >
              {formattedPrice}
            </Text>
          </Stack>
        </Stack>
        <UnifiedPriceTagPriceBreakdown {...props} />
      </Stack>
      <UnifiedPriceTagPricePerAdultExplanation {...props} />
      <UnifiedPriceTagExplanation {...props} />
    </Stack>
  );
};
